import { useCallback, useMemo, useState } from 'react';
import { Grid, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { WizardContainer } from '../../../components/wizard/Wizard';
import { WizardProvider } from '../../../components/wizard/useWizard';
import { AvailabilityAfterProgramStep } from './AvailabilityAfterProgramStep';
import { CharacterAndHabitsStep } from './CharacterAndHabitsStep';
import { DietaryHabitsAndHealthStep } from './DietaryHabitsAndHealthStep';
import { SupportStep } from './SupportStep';
import {
	FamilyMemberInterviewProvider,
	useFamilyMemberInterviewWizard,
} from './useFamilyMemberInterviewWizard';
import { useInterviewLandingPage } from '../InterviewLandingPage/useInterviewLandingPage';
import {
	FAMILY_CONTEXT,
	INTERVIEW_LANDING,
	VIEW_FAMILY_MEMBER_INTERVIEW,
} from '../../../shared/Routes';
import Icon from '../../../components/ui-components/Icon';
import LeftArrow from '../../../assets/images/arrow-left.svg';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';
import { useLocation } from 'react-router-dom';
import useUserSession from '../../../hooks/useUserSession';
import { SELECTED_ROLES } from '../../../shared/Utils';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const FamilyMemberInterviewWizard = () => {
	const { onSubmitClick, application } = useFamilyMemberInterviewWizard();
	const { t } = useTranslation();
	const { navigateToPage } = useInterviewLandingPage();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { selectedRole } = useUserSession();
	const { pathname } = useLocation();
	const isProposer = selectedRole === UserRole.PROPOSER;
	const isRoleAllowed = SELECTED_ROLES.includes(selectedRole as UserRole);

	const isViewable = pathname.includes(VIEW_FAMILY_MEMBER_INTERVIEW);
	const isProposerAndViewable = isProposer && isViewable;
	const currentWizard = 0;
	const stepOffset = 0;

	const wizards = useMemo(
		() => [
			{
				steps: [
					<CharacterAndHabitsStep key={1} />,
					<AvailabilityAfterProgramStep key={2} />,
					<SupportStep key={3} />,
					<DietaryHabitsAndHealthStep key={4} />,
				],
				descriptions: [
					{
						title: t('characterAndHabits'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('availabilityAfterProgram'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('familySupport'),
						description: t('exploreIfConditionsAroundAreConducive') ?? '',
					},
					{
						title: t('dietaryHabitsAndHealth'),
						description: t('dietaryHabitsAndHealth') ?? '',
					},
				],
			},
		],
		[t]
	);
	const steps = wizards[currentWizard].steps;
	const descriptions = wizards[currentWizard].descriptions;
	const [stepStates, setStepStates] = useState<number[]>(() => {
		const draftIndex = 0;
		return new Array(wizards.length)
			.fill(0)
			.map((_, index) =>
				index === currentWizard ? Math.max(0, draftIndex) : 0
			);
	});

	const showModal = useCallback(async (): Promise<boolean> => {
		return Promise.resolve(true);
	}, []);

	const customHandlePrevious = useCallback(() => {
		if (stepStates[currentWizard] === 0) {
			navigateToPage(isViewable ? INTERVIEW_LANDING : FAMILY_CONTEXT);
		}
	}, [stepStates, navigateToPage, isViewable]);

	const completedIndex = application?.familyMemberInterviewDone
		? steps.length
		: 0;

	const handleEvaluateClick = useCallback(() => {
		navigateToPage(INTERVIEW_LANDING);
	}, [navigateToPage]);

	return (
		<div
			className={`flex flex-col h-full min-h-0 p-[8px] ${
				!isMobile ? 'w-fit my-0 mx-auto' : 'w-full my-0 mx-auto'
			}`}
		>
			<div className="relative flex justify-between items-center w-full">
				<div
					className="flex font-sans justify-center gap-1 mb-1 items-center cursor-pointer absolute left-0"
					onClick={handleEvaluateClick}
				>
					<Icon src={LeftArrow} width={20} height={20} />
					{!isMobile && (
						<Text className="font-bold text-sm text-primary-blue whitespace-nowrap">
							{t('backToEvaluate')}
						</Text>
					)}
				</div>
				<Text className="flex-grow mt-4 mb-6 font-sans font-bold text-sm text-center text-secondary-black px-10">
					{t('familyMemberInterview')}
					{isViewable && ` (${t('viewOnly')})`}
				</Text>
			</div>
			<WizardContainer
				onSubmitClick={
					isRoleAllowed ? () => void handleEvaluateClick() : onSubmitClick
				}
				onNextClick={showModal}
				onPreviousClick={customHandlePrevious}
				steps={steps}
				stepDescription={descriptions}
				stepOffset={stepOffset}
				initialStepState={stepStates[currentWizard] || 0}
				onStepStateChange={(newState: number) => {
					setStepStates((prev) => {
						const updated = [...prev];
						updated[currentWizard] = newState;
						return updated;
					});
				}}
				currentWizard={currentWizard}
				draftIndex={completedIndex}
				completedDraftIndex={completedIndex}
				isProposerAndViewable={isProposerAndViewable}
			/>
		</div>
	);
};

export const FamilyMemberInterviewWizardContainer = () => {
	return (
		<FamilyMemberInterviewProvider>
			<WizardProvider>
				<FamilyMemberInterviewWizard />
			</WizardProvider>
		</FamilyMemberInterviewProvider>
	);
};
